import React, { useEffect, useMemo, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import { Map as MapIcon, UploadCloud, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MapsTab({ blocks }) {
  const [maps, setMaps] = useState(null);
  const [ranch, setRanch] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => setMaps((await api.get("/irrigation/maps")).data);
  useEffect(() => { load(); }, []);

  const vineyards = useMemo(() => [...new Map(blocks.map((b) => [b.ranch_number, b])).values()], [blocks]);

  const upload = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ranch_number", ranch);
      fd.append("title", title);
      await api.post("/irrigation/maps", fd);
      setTitle(""); setFile(null);
      document.getElementById("map-file-input").value = "";
      toast.success("Map uploaded");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const view = async (m) => {
    const token = getToken();
    const res = await fetch(`${API}/irrigation/maps/${m.id}/file`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank");
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    await api.delete(`/irrigation/maps/${m.id}`);
    await load();
  };

  if (!maps) return <div className="p-6 font-bold uppercase text-sm">Loading maps…</div>;

  return (
    <div className="space-y-3" data-testid="irr-maps-tab">
      <div className="bg-white border border-stone-200 p-4 space-y-3">
        <div className="font-bold uppercase tracking-wide text-sm flex items-center gap-2"><UploadCloud size={15} /> Upload Ranch Map</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select data-testid="map-ranch-select" value={ranch} onChange={(e) => setRanch(e.target.value)} className="h-11 px-3 border border-stone-300 text-sm bg-white">
            <option value="">— Select Ranch —</option>
            {vineyards.map((r) => <option key={r.ranch_number} value={r.ranch_number}>{r.ranch_number} · {r.name}</option>)}
          </select>
          <input data-testid="map-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="h-11 px-3 border border-stone-300 text-sm" placeholder="e.g. Irrigation block map 2026" />
          <input id="map-file-input" data-testid="map-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm self-center" />
        </div>
        <button data-testid="map-upload-btn" onClick={upload} disabled={busy || !file || !ranch || !title.trim()}
          className="h-11 px-5 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40 flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload Map
        </button>
      </div>

      <div className="space-y-2">
        {maps.map((m) => (
          <div key={m.id} data-testid={`map-doc-${m.id}`} className="bg-white border border-stone-200 p-4 flex flex-wrap items-center gap-3">
            <MapIcon size={18} className="text-[#7C9673] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold">{m.title}</div>
              <div className="text-xs text-stone-500">Ranch {m.ranch_number} · {m.filename} · {m.created_at?.slice(0, 10)}</div>
            </div>
            <button data-testid={`map-view-${m.id}`} onClick={() => view(m)} className="h-10 px-3 border border-stone-300 flex items-center gap-1 text-xs font-bold uppercase"><ExternalLink size={13} /> View</button>
            <button data-testid={`map-delete-${m.id}`} onClick={() => remove(m)} className="h-10 w-10 border border-stone-300 flex items-center justify-center text-red-700"><Trash2 size={14} /></button>
          </div>
        ))}
        {maps.length === 0 && <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="maps-empty">No maps uploaded yet</div>}
      </div>
    </div>
  );
}
